import { useEffect, useState } from "react";
import { Gift, CircleHelp, Star, Lock } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { fmt, api } from "@/lib/api";
import { getBest, getClaim } from "@/lib/claimStore";

function MiniGrid() {
  const [lit, setLit] = useState([0, 6, 9, 11, 12]);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      setLit(Array.from({ length: 5 }, () => Math.floor(Math.random() * 16)));
    }, 1600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="grid grid-cols-4 gap-1.5 p-3 rounded-2xl bg-[#1b1a18] border border-white/5" aria-hidden="true">
      {Array.from({ length: 16 }, (_, i) => (
        <span key={i} className={`mini-cell ${lit.includes(i) ? "lit" : ""}`} />
      ))}
    </div>
  );
}

function useRecord(initial) {
  const [rec, setRec] = useState(0);
  useEffect(() => {
    const load = () => !document.hidden && api.get("/game/record").then((r) => setRec(r.data.record)).catch(() => {});
    const onBeat = (e) => setRec((r) => Math.max(r, e.detail));
    const es = typeof EventSource !== "undefined" ? new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/game/record/stream`) : null;
    if (es) es.onmessage = (e) => onBeat({ detail: Number(e.data) || 0 });
    const id = setInterval(load, 30000);
    document.addEventListener("visibilitychange", load);
    window.addEventListener("lm:score", onBeat);
    return () => { es?.close(); clearInterval(id); document.removeEventListener("visibilitychange", load); window.removeEventListener("lm:score", onBeat); };
  }, []);
  return Math.max(rec, initial || 0);
}

export const HeroGameCard = () => {
  const { config, openGame } = useSite();
  const g = config.game;
  const best = getBest();
  const record = useRecord(config.record);
  const claim = getClaim();
  const pct = Math.min(100, (record / g.target_score) * 100);

  return (
    <div data-testid="hero-game-card" className="panel-dark panel-glass rounded-[22px] p-5 sm:p-6 w-full max-w-[420px] fade-up" style={{ animationDelay: ".25s" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Gift className="text-[#D6AD62] shrink-0" size={24} />
          <h2 className="font-display text-[17px] sm:text-lg font-semibold leading-tight">Kiçik bir oyun, böyük fürsət!</h2>
        </div>
        <button onClick={() => openGame("rules")} data-testid="hero-game-help-btn" aria-label="Oyun qaydaları"
          className="w-8 h-8 shrink-0 rounded-full border border-white/25 flex items-center justify-center hover:border-[#D6AD62] transition-colors">
          <CircleHelp size={16} />
        </button>
      </div>
      <p className="text-sm text-[#cfc9bd] mt-2 mb-5">{fmt(g.target_score)} xal topla və hədiyyə qutusu qazan!</p>
      <div className="grid grid-cols-[1.1fr_1fr] gap-3">
        <MiniGrid />
        <div className="rounded-2xl bg-[#1b1a18] border border-white/5 p-4 flex flex-col justify-between">
          <div>
            <p className="text-xs text-[#a9a398]">Saytın rekordu</p>
            <p className="flex items-center gap-2 mt-2 font-display text-xl font-bold" data-testid="hero-record-score">
              <span className="w-6 h-6 rounded-full bg-[#D6AD62] flex items-center justify-center"><Star size={13} className="text-[#111110]" fill="#111110" /></span>
              <span key={record} className="rec-bump inline-block">{fmt(record)}</span>
            </p>
            <p className="text-[11px] text-[#a9a398] mt-1.5" data-testid="hero-best-score">Sizin nəticəniz: <span className="text-[#F3EEE4] font-semibold">{fmt(best)}</span></p>
          </div>
          <div>
            <div className="progress-gold mt-4"><span style={{ width: `${pct}%` }} /></div>
            <p className="text-[11px] text-[#a9a398] mt-2">Hədəf: {fmt(g.target_score)}</p>
          </div>
        </div>
      </div>
      {g.enabled ? (
        <button
          data-testid={claim ? "hero-claim-btn" : "hero-play-btn"}
          onClick={() => openGame(claim ? "claim" : "play")}
          className="btn-gold w-full mt-4 !rounded-xl"
        >
          {claim ? (claim.prize ? "Hədiyyəni əldə et" : "Hədiyyə qutusunu seç") : best ? "Yenidən oyna" : "Oyuna başla"}
        </button>
      ) : (
        <p className="mt-4 text-sm text-[#a9a398] flex items-center gap-2" data-testid="hero-game-disabled"><Lock size={14} /> Oyun müvəqqəti deaktivdir</p>
      )}
      <p className="text-[11px] text-[#8b867c] mt-3 text-center">Sənin uğurun — bizim üçün dəyərlidir.</p>
    </div>
  );
};
